// import cx from "classnames";
import { Modal } from "../../../components/Modal";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { PostCompose } from "../../../components/PostCompose";
import {
  FieldValues,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  useForm,
} from "react-hook-form";
import { ImageThumbnail } from "../../../components/ImageThumbnail";
import { getFeeds } from "../../../api/feeds";
import { handleError } from "../../../utils/handleError";
import { schedule } from "../../../api/schedule";
import { schedule_update } from "../../../api/schedule_update";
import {
  getImageDerivativeManifest,
  getPanoramaGeneratedPaths,
  getPanoramaSourceManifests,
  getSignedUrl,
} from "../../../api/files";
import {
  PostSelection,
  AddFiles,
  ResetSelectedFiles,
  SelectedFile,
  SetSelectedPost,
  SetSelection,
  Step,
  UpdateFile,
  UpdatePosts,
} from "../../../types/PostSelection";
import { Post } from "../../../types/api/Post";
import { Feed, Feeds, TypeMapping } from "../../../types/api/Feed";
import { PhotoViewer } from "../../../components/PhotoViewer";
import { SavedContent } from "../../../types/api/SavedContent";
import {
  Check,
  MoreVertical,
  Trash,
  XCircle,
  Download,
  Zap,
} from "react-feather";
import { PostDisplay } from "../../../components/PostDisplay";
import { schedule_delete } from "../../../api/schedule_delete";
import { ConfirmationModal } from "../../../components/_ui/ConfirmationModal";
import {
  getDefaultDate,
  getNextAvailablePostingSlot,
} from "../../../utils/postingSchedule";
import { autoAddSavedContentToForm } from "../../../utils/autoAddSavedContent";
import { download_files } from "../../../utils/downloadFiles";
import { generateAltText } from "../../../api/altText";
import { useSavedDescriptions } from "../../../state/db/savedDescriptions";
import {
  setInitialSelectedSavedDescriptions,
  useSelectedSavedDescriptions,
} from "../../../state/hooks/useSelectedSavedDescriptions";
import { PositionStates } from "../../../components/PostCompose/usePositionManagement";
import { resolvePostMediaSelections } from "../../../api/helper/resolvePostMediaSelections";

export function CreatePostModal({
  selection: { openModal, selected, step, isExisting, currentPost },
  setSelection,
  updatePosts,
  selectedPost,
  setSelectedPost,
  updateFile,
  resetSelectedFiles,
  availableFiles,
  refreshFileUsage,
  addFiles,
}: {
  selection: PostSelection;
  setSelection: SetSelection;
  updatePosts: UpdatePosts;
  selectedPost: Post | null;
  setSelectedPost: SetSelectedPost;
  updateFile: UpdateFile;
  resetSelectedFiles: ResetSelectedFiles;
  availableFiles: SelectedFile[];
  refreshFileUsage: (ids: string[]) => void;
  addFiles: AddFiles;
}) {
  const [feeds, setFeeds] = useState<Feeds>([]);
  const { savedDescriptions } = useSavedDescriptions();
  const [openViewer, setOpenViewer] = useState(false);
  const [viewerSelection, setViewerSelection] = useState<string | null>(null);
  const [isMediaSelectorOpen, setIsMediaSelectorOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isGeneratingAltText, setIsGeneratingAltText] = useState(false);
  const [autoAddRan, setAutoAddRan] = useState(false);
  const [autoAltTextRan, setAutoAltTextRan] = useState(false);
  const [hashtagsGenerated, setHashtagsGenerated] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const proceedAnywayRef = useRef(false);
  const { selectedSavedDescriptions, setSelectedSavedDescriptions } =
    useSelectedSavedDescriptions();

  const { register, watch, handleSubmit, setValue, getValues, reset } =
    useForm<FieldValues>();

  useEffect(() => {
    getFeeds()
      .then((files) => setFeeds(files))
      .catch((err) => handleError(err));

    if (!isExisting && !selectedPost) {
      // Set default date based on user's posting schedule
      console.log("🎯 Setting default post date for new post...");
      getNextAvailablePostingSlot()
        .then((nextSlot) => {
          console.log(
            "📅 Next available posting slot:",
            nextSlot.toISOString(),
          );
          setValue("date", nextSlot.toISOString().slice(0, 16));
        })
        .catch((error) => {
          console.warn(
            "Failed to get next posting slot, using default:",
            error,
          );
          setValue("date", getDefaultDate().toISOString().slice(0, 16));
        });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset hashtag generation tracking when modal opens
  useEffect(() => {
    if (openModal) {
      setHashtagsGenerated(false);
      setAutoAltTextRan(false);
      proceedAnywayRef.current = false;
    }
  }, [openModal]);

  const handleMediaSelectionChange = useCallback(
    (updatedFiles: SelectedFile[]) => {
      // Update the selected files in the parent state
      const selectedFiles = updatedFiles
        .filter((f) => f.isSelected)
        .sort((a, b) => a.selectedAt! - b.selectedAt!);
      setSelection((s) => ({
        ...s,
        selected: selectedFiles,
      }));

      // Update individual file states
      updatedFiles.forEach((file) => {
        updateFile(file.name, {
          isSelected: file.isSelected,
          selectedAt: file.selectedAt,
          altText: file.altText,
        });
      });
    },
    [setSelection, updateFile],
  );

  const updateMedia = useCallback(() => {
    setIsMediaSelectorOpen(true);
  }, []);

  const generateAltTextForImages = useCallback(async () => {
    if (selected.length === 0) return;

    // Filter images that don't have alt text
    const imagesNeedingAltText = selected.filter(
      (img) => !img.altText || img.altText.trim() === "",
    );

    if (imagesNeedingAltText.length === 0) {
      // All images already have alt text
      return;
    }

    setIsGeneratingAltText(true);
    try {
      const imageUrls = imagesNeedingAltText.map((img) => img.url);
      const result = await generateAltText({ imageUrls });

      if (result.error) {
        handleError(new Error(result.error));
        return;
      }

      // Create a map of generated alt texts by image URL for easier lookup
      const altTextMap = new Map();
      imagesNeedingAltText.forEach((img, index) => {
        altTextMap.set(img.url, result.altTexts[index] || "");
      });

      // Update all selected files, only changing those that needed alt text
      const updatedFiles = selected.map((file) => ({
        ...file,
        altText: altTextMap.has(file.url)
          ? altTextMap.get(file.url)
          : file.altText || "",
      }));

      setSelection((s) => ({
        ...s,
        selected: updatedFiles,
      }));

      // Update individual file states only for files that got new alt text
      imagesNeedingAltText.forEach((file, index) => {
        updateFile(file.name, {
          altText: result.altTexts[index] || "",
        });
      });
    } catch (error) {
      handleError(error as Error);
    } finally {
      setIsGeneratingAltText(false);
    }
  }, [selected, setSelection, updateFile]);

  const nextStep = useCallback(() => {
    if (step === 1) {
      const values = getValues("default");

      // Validate that location and description are present and hashtags were generated
      const hasLocation = !!values.location;
      const hasDescription =
        values.post_content && values.post_content.trim() !== "";

      if (!hasLocation || !hasDescription || !hashtagsGenerated) {
        // If user hasn't confirmed to proceed anyway, show validation modal
        if (!proceedAnywayRef.current) {
          console.log("Validation failed", {
            hasLocation,
            hasDescription,
            hashtagsGenerated,
          });
          setShowValidationModal(true);
          return;
        }
        // Reset the ref for next time
        proceedAnywayRef.current = false;
      }

      for (const feed of feeds) {
        console.log(
          "state - SET INITIAL SELECTED SAVED DESCRIPTIONS",
          feed.slug,
          selectedSavedDescriptions,
        );

        setValue(
          `${feed.slug}.selectedSavedDescriptions`,
          selectedSavedDescriptions,
        );

        // Update initial values
        setInitialSelectedSavedDescriptions(
          `selectedSavedDescriptions-${feed.slug}`,
          selectedSavedDescriptions,
        );

        const type_mapping = feed.type_mapping as TypeMapping;
        setValue(
          `${feed.slug}.post_content`,
          replaceVariants(values.post_content, feed.slug!, savedDescriptions),
        );
        setValue(
          `${feed.slug}.type`,
          type_mapping[values.type]?.type || values.type,
        );
        if (feed.auto_publish) {
          setValue(`${feed.slug}.auto_publish`, true);
        }
        setValue(`${feed.slug}.location`, values.location);

        setValue(
          `${feed.slug}.hashtags`,
          addHashtagsVariants(
            values.hashtags ?? [],
            feed.slug!,
            savedDescriptions,
          ),
        );
      }

      setSelection((s) => ({ ...s, step: 2 }));
    }
  }, [
    setSelection,
    feeds,
    setValue,
    getValues,
    step,
    selectedSavedDescriptions,
    savedDescriptions,
    hashtagsGenerated,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    async function runAsync() {
      if (!openModal && selectedPost) {
        const url = new URL(window.location.href);
        url.searchParams.set("postId", selectedPost.id.toString());
        window.history.pushState({}, "", url);

        const date = new Date(selectedPost.publish_at!).getTime();
        const diff = new Date().getTimezoneOffset() * 60 * 1000;
        const dateWithTimezone = new Date(date - diff);

        setValue("date", dateWithTimezone.toISOString().slice(0, 16));
        setValue("disable_auto_publish", !!selectedPost.disable_auto_publish);

        let selectedImages: SelectedFile[] = [];
        const allPostImages: SelectedFile[] = [];
        resetSelectedFiles();

        if (selectedPost.posts_media) {
          const generatedPaths = await getPanoramaGeneratedPaths(
            selectedPost.posts_media.flatMap((media) =>
              media.src_original ? [media.src_original] : [],
            ),
          );

          for (const media of selectedPost.posts_media) {
            const imageDerivatives = media.mine_type?.startsWith("video/")
              ? undefined
              : await getImageDerivativeManifest(media.src_original!);

            const selectedImage = {
              id: media.id,
              name: media.title!,
              isSelected: true,
              mine_type: media.mine_type!,
              url: await getSignedUrl(media.src_thumbnail!),
              selectedAt: Date.now(),
              altText: media.alt_text || "",
              imageDerivatives,
            };
            allPostImages.push(selectedImage);

            if (
              !media.src_original ||
              !generatedPaths.has(media.src_original)
            ) {
              selectedImages.push(selectedImage);
              updateFile(media.title!, {
                isSelected: true,
                selectedAt: Date.now(),
                altText: media.alt_text || "",
                imageDerivatives,
              });
            }
          }
        }

        setSelection((s) => ({
          ...s,
          step: 2,
          selected: selectedImages,
          last_update: Date.now(),
          isExisting: true,
          openModal: true,
          currentPost: selectedPost,
        }));

        if (selectedPost.posts_for_feeds) {
          for (const post_for_feed of selectedPost.posts_for_feeds) {
            setValue(
              `${(post_for_feed.feed as Feed).slug}.post_content`,
              post_for_feed.content,
            );
            setValue(
              `${(post_for_feed.feed as Feed).slug}.type`,
              post_for_feed.type,
            );

            if ((post_for_feed.feed as Feed).auto_publish) {
              setValue(
                `${(post_for_feed.feed as Feed).slug}.auto_publish`,
                post_for_feed.auto_publish,
              );
            }

            setValue(
              `${(post_for_feed.feed as Feed).slug}.location`,
              post_for_feed.location,
            );

            setValue(
              `${(post_for_feed.feed as Feed).slug}.hashtags`,
              post_for_feed.hashtags,
            );

            // Set positions if they exist, otherwise set default empty positions
            setValue(
              `${(post_for_feed.feed as Feed).slug}.positions`,
              (post_for_feed.positions as unknown as PositionStates) || {
                content: {},
                hashtag: {},
                tag: {},
              },
            );

            // Set selectedSavedDescriptions if they exist, otherwise set default empty array
            setValue(
              `${(post_for_feed.feed as Feed).slug}.selectedSavedDescriptions`,
              (post_for_feed.selected_saved_descriptions as number[]) || [],
            );

            // Sync the hook state with the form values for existing posts
            setInitialSelectedSavedDescriptions(
              `selectedSavedDescriptions-${(post_for_feed.feed as Feed).slug}`,
              (post_for_feed.selected_saved_descriptions as number[]) || [],
            );

            const userSelectedFiles: SelectedFile[] = [];

            console.log(
              "POST FOR FEED",
              post_for_feed.selectedFiles,
              selectedPost.posts_media,
            );

            for (const postMediaId of post_for_feed.selectedFiles ?? []) {
              const media = selectedPost.posts_media?.find(
                (m) => m.id === postMediaId,
              );
              const foundMedia = allPostImages.find((m) => m.id === media?.id);

              console.log("FOUND MEDIA", postMediaId, media, foundMedia);

              if (foundMedia) {
                userSelectedFiles.push(foundMedia);
              }
            }

            console.log("USER SELECTED FILES", userSelectedFiles);

            setValue(
              `${(post_for_feed.feed as Feed).slug}.selected_files`,
              userSelectedFiles,
            );
          }
        }
      }
    }
    runAsync();
  }, [selectedPost, currentPost]); // eslint-disable-line react-hooks/exhaustive-deps

  const cancel = useCallback(
    function () {
      const url = new URL(window.location.href);
      url.searchParams.delete("postId");
      window.history.pushState({}, "", url);

      setSelectedPost(null);
      setSelection((s) => ({
        ...s,
        step: 1,
        selected: [],
        openModal: false,
        isExisting: false,
        currentPost: null,
      }));
      reset({ default: { post_content: "", type: "" } });
      resetSelectedFiles();
    },
    [setSelectedPost, setSelection, reset, resetSelectedFiles],
  );

  const onClose = useCallback(() => {
    setSelection((s) => ({ ...s, openModal: false }));
    setSelectedSavedDescriptions(() => []);
    cancel();
  }, [cancel, setSelection, setSelectedSavedDescriptions]);

  useEffect(() => {
    const postType = getPossibleTypes(selected);
    console.log(
      "SELECTED CHANGE",
      postType,
      getValues("default.type"),
      !getValues("default.type"),
    );
    if (postType && !getValues("default.type")) {
      setValue("default.type", postType[0]);
    }
  }, [selected, getValues, setValue]);

  // Auto-add saved content when photos are selected for a new post
  useEffect(() => {
    if (
      !autoAddRan &&
      openModal &&
      selected.length > 0 &&
      !isExisting &&
      feeds.length > 0
    ) {
      setAutoAddRan(true);

      console.log("Checking for auto-add saved content...");

      autoAddSavedContentToForm(selected, setSelectedSavedDescriptions).catch(
        (error) => {
          console.error("Error auto-adding saved content:", error);
          handleError(error as Error);
        },
      );
    }
  }, [
    openModal,
    selected.length,
    isExisting,
    feeds,
    autoAddRan,
    selected,
    setValue,
    watch,
    setSelectedSavedDescriptions,
  ]);

  // Auto-generate alt text for images that don't have it
  useEffect(() => {
    if (
      !openModal ||
      selected.length === 0 ||
      isGeneratingAltText ||
      autoAltTextRan
    )
      return;

    const imagesNeedingAltText = selected.filter(
      (img) =>
        img.mine_type?.startsWith("image/") &&
        (!img.altText || img.altText.trim() === ""),
    );

    if (imagesNeedingAltText.length === 0) return;

    // Auto-generate alt text in the background
    const autoGenerateAltText = async () => {
      console.log(
        "Auto-generating alt text for",
        imagesNeedingAltText.length,
        "images",
      );

      setIsGeneratingAltText(true);
      setAutoAltTextRan(true);
      try {
        const imageUrls = imagesNeedingAltText.map((img) => img.url);
        const result = await generateAltText({ imageUrls });

        if (result.error) {
          console.warn("Failed to auto-generate alt text:", result.error);
          return;
        }

        // Create a map of generated alt texts by image URL for easier lookup
        const altTextMap = new Map();
        imagesNeedingAltText.forEach((img, index) => {
          altTextMap.set(img.url, result.altTexts[index] || "");
        });

        // Update all selected files, only changing those that needed alt text
        const updatedFiles = selected.map((file) => ({
          ...file,
          altText: altTextMap.has(file.url)
            ? altTextMap.get(file.url)
            : file.altText || "",
        }));

        setSelection((s) => ({
          ...s,
          selected: updatedFiles,
        }));

        // Update individual file states only for files that got new alt text
        imagesNeedingAltText.forEach((file, index) => {
          updateFile(file.name, {
            altText: result.altTexts[index] || "",
          });
        });

        console.log(
          "Auto-generated alt text for",
          imagesNeedingAltText.length,
          "images",
        );
      } catch (error) {
        console.warn("Failed to auto-generate alt text:", error);
        // Don't show error to user for auto-generation failures
      } finally {
        setIsGeneratingAltText(false);
      }
    };

    // Debounce the auto-generation to avoid rapid API calls
    const timer = setTimeout(autoGenerateAltText, 1000);
    return () => clearTimeout(timer);
  }, [
    selected,
    openModal,
    isGeneratingAltText,
    autoAltTextRan,
    setSelection,
    updateFile,
  ]);

  async function deletePost() {
    if (!currentPost) return;

    setIsDeleting(true);
    try {
      await schedule_delete(currentPost);
      updatePosts();
      cancel();
    } catch (error) {
      handleError(error as Error);
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
    }
  }

  const downloadFiles = async () => {
    if (currentPost?.posts_media) {
      try {
        const panoramaSources = await getPanoramaSourceManifests(
          currentPost.posts_media.flatMap((media) =>
            media.src_original ? [media.src_original] : [],
          ),
        );
        const sourceDownloads = panoramaSources.map((manifest) => {
          const download =
            manifest.derivatives["3600px"]?.path || manifest.originalPath;
          return {
            download,
            fallback:
              manifest.derivatives["2400px"]?.path ||
              manifest.derivatives["960px"]?.path,
            filename: download.slice(download.lastIndexOf("/") + 1),
          };
        });
        const postDownloads = currentPost.posts_media.flatMap((media) => {
          const download =
            media.src_3600 || media.src_2400 || media.src_original;
          if (!download) return [];

          return [
            {
              download,
              fallback: media.src_2400 || media.src_960 || media.src_original,
              filename:
                media.title || download.slice(download.lastIndexOf("/") + 1),
            },
          ];
        });
        const downloads = [
          ...new Map(
            [...sourceDownloads, ...postDownloads].map((file) => [
              file.download,
              file,
            ]),
          ).values(),
        ];

        await download_files(downloads);
      } catch (error) {
        handleError(error as Error);
      }
    }
  };

  async function onSubmit(values: FieldValues) {
    setIsSaving(true);
    try {
      const { allFiles } = resolvePostMediaSelections(feeds, values, selected);

      if (isExisting) {
        await schedule_update(feeds, values, selected, selectedPost!);
      } else {
        await schedule(feeds, values, selected);
      }

      updatePosts();

      // Refresh file usage status after successful post creation/update
      refreshFileUsage(
        allFiles
          .map((file) => file._id)
          .filter((id): id is string => typeof id === "string"),
      );

      cancel();
    } catch (e) {
      handleError(e as Error);
    } finally {
      setIsSaving(false);
    }
  }

  const postTypes = getPossibleTypes(selected) || [];

  const selectImage = (id: string) => {
    setViewerSelection(id);
    setOpenViewer(true);
  };

  const closeImageViwer = () => {
    setViewerSelection(null);
    setOpenViewer(false);
  };

  return (
    <>
      <Modal
        show={openModal}
        title={"Create Post"}
        onClose={onClose}
        onSubmit={handleSubmit(onSubmit)}
        footer={
          <Footer
            selected={selected}
            watch={watch}
            nextStep={nextStep}
            step={step}
            isExisting={isExisting!}
            currentPost={currentPost!}
            onClose={onClose}
            isSaving={isSaving}
            deletePost={() => setIsDeleteModalOpen(true)}
          />
        }
      >
        <>
          <div className="flex flex-col md:flex-row gap-4 md:gap-8">
            <div className="flex-1 w-full md:max-w-96 top-0">
              <div className="prose mb-2">
                <h2>Publish Date & Time</h2>
              </div>

              <input
                className="input input-lg w-full mb-4"
                type="datetime-local"
                {...register("date")}
              />

              <div className="form-control mb-4">
                <label className="label cursor-pointer justify-start gap-3">
                  <input
                    type="checkbox"
                    className="toggle toggle-error"
                    {...register("disable_auto_publish")}
                  />
                  <span className="label-text">
                    Disable Auto-Publishing for this post
                  </span>
                </label>
              </div>

              <div className="prose mb-2">
                <h2>Photos & Videos</h2>
              </div>

              <div className="flex justify-start flex-wrap gap-4 mb-4">
                {selected.map((img) => (
                  <ImageThumbnail
                    key={img.id}
                    id={img.id!}
                    title={img.name}
                    url={img.url}
                    minetype={img.mine_type}
                    onSelect={() => selectImage(img._id!)}
                  />
                ))}
              </div>

              <div className="flex gap-2 mb-6">
                <div>
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    onClick={updateMedia}
                  >
                    Update Media
                  </button>
                </div>
                {isExisting &&
                  currentPost?.posts_media &&
                  currentPost.posts_media.length > 0 && (
                    <div>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={downloadFiles}
                      >
                        <Download size={16} />
                        Download Files
                      </button>
                    </div>
                  )}
              </div>

              {selected.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="prose">
                      <h3>Alt Text for Images</h3>
                    </div>
                    {(() => {
                      const imagesNeedingAltText = selected.filter(
                        (img) => !img.altText || img.altText.trim() === "",
                      );
                      const hasImagesNeedingAltText =
                        imagesNeedingAltText.length > 0;

                      return (
                        <button
                          type="button"
                          className={`btn btn-xs ${
                            hasImagesNeedingAltText
                              ? "btn-outline"
                              : "btn-disabled"
                          }`}
                          onClick={generateAltTextForImages}
                          disabled={
                            isGeneratingAltText || !hasImagesNeedingAltText
                          }
                          title={
                            !hasImagesNeedingAltText
                              ? "All images already have alt text"
                              : `Generate alt text for ${
                                  imagesNeedingAltText.length
                                } image${
                                  imagesNeedingAltText.length > 1 ? "s" : ""
                                }`
                          }
                        >
                          {isGeneratingAltText ? (
                            <span className="loading loading-spinner loading-xs"></span>
                          ) : (
                            <Zap size={14} />
                          )}
                          {isGeneratingAltText
                            ? "Generating..."
                            : hasImagesNeedingAltText
                              ? `Generate Alt Text (${imagesNeedingAltText.length})`
                              : "All Set ✓"}
                        </button>
                      );
                    })()}
                  </div>
                  <div className="space-y-3">
                    {selected.map((img, index) => (
                      <div key={img.id} className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-base-content/70">
                          {img.name}
                        </label>
                        <input
                          type="text"
                          className="input input-sm w-full"
                          placeholder="Describe this image for accessibility..."
                          value={img.altText || ""}
                          onChange={(e) => {
                            const updatedFiles = selected.map((file) =>
                              file.id === img.id
                                ? { ...file, altText: e.target.value }
                                : file,
                            );
                            setSelection((s) => ({
                              ...s,
                              selected: updatedFiles,
                            }));

                            updateFile(img.name, {
                              altText: e.target.value,
                            });
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* main content full width on mobile */}
            <div className="flex-1 w-full">
              {step === 1 && (
                <DefaultPost
                  register={register}
                  watch={watch}
                  setValue={setValue}
                  postTypes={postTypes}
                  selectedFiles={selected}
                  isModalOpened={openModal}
                  onHashtagsGenerated={() => setHashtagsGenerated(true)}
                />
              )}

              {step === 2 && (
                <PostPerFeeds
                  feeds={feeds}
                  register={register}
                  watch={watch}
                  setValue={setValue}
                  postTypes={postTypes}
                  selectedFiles={selected}
                  isModalOpened={openModal}
                  isEditing={false}
                  currentPost={currentPost}
                  updatePosts={updatePosts}
                  addFiles={addFiles}
                />
              )}
            </div>
          </div>
        </>
      </Modal>

      {openViewer && !isMediaSelectorOpen && (
        <PhotoViewer
          open={openViewer}
          close={closeImageViwer}
          selected={viewerSelection}
          images={selected}
        />
      )}

      {isMediaSelectorOpen && (
        <PhotoViewer
          open={isMediaSelectorOpen}
          close={() => setIsMediaSelectorOpen(false)}
          selected={null}
          images={availableFiles}
          onSelectionChange={handleMediaSelectionChange}
        />
      )}

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={deletePost}
        title="Delete Post"
        message="Are you sure you want to delete this post? This action cannot be undone."
        confirmText="Delete"
        isLoading={isDeleting}
      />

      <ConfirmationModal
        isOpen={showValidationModal}
        onClose={() => setShowValidationModal(false)}
        onConfirm={() => {
          proceedAnywayRef.current = true;
          setShowValidationModal(false);
          nextStep();
        }}
        title="Missing Required Information"
        message={(() => {
          const values = getValues("default") || {};
          const hasLocation =
            values.location &&
            typeof values.location === "string" &&
            values.location.trim() !== "";
          const hasDescription =
            values.post_content &&
            typeof values.post_content === "string" &&
            values.post_content.trim() !== "";

          const missing = [];
          if (!hasLocation) missing.push("location");
          if (!hasDescription) missing.push("description");
          if (!hashtagsGenerated) missing.push("hashtags");

          if (missing.length === 1) {
            return `You haven't provided a ${missing[0]}. Do you want to continue anyway?`;
          } else {
            return `You haven't provided a ${missing
              .slice(0, -1)
              .join(", ")} or ${
              missing[missing.length - 1]
            }. Do you want to continue anyway?`;
          }
        })()}
        confirmText="Continue Anyway"
        cancelText="Go Back"
      />
    </>
  );
}

function DefaultPost({
  register,
  watch,
  setValue,
  postTypes,
  selectedFiles,
  isModalOpened,
  onHashtagsGenerated,
}: {
  register: UseFormRegister<FieldValues>;
  watch: UseFormWatch<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  postTypes: string[];
  selectedFiles: SelectedFile[];
  isModalOpened: boolean;
  onHashtagsGenerated?: () => void;
}) {
  return (
    <PostCompose
      register={register}
      watch={watch}
      setValue={setValue}
      name="default"
      postTypes={postTypes}
      selectedFiles={selectedFiles}
      isModalOpened={isModalOpened}
      onHashtagsGenerated={onHashtagsGenerated}
    />
  );
}

function PostPerFeeds({
  feeds,
  register,
  watch,
  setValue,
  postTypes,
  selectedFiles,
  isModalOpened,
  isEditing,
  currentPost,
  updatePosts,
  addFiles,
}: {
  feeds: Feeds;
  register: UseFormRegister<FieldValues>;
  watch: UseFormWatch<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  postTypes: string[];
  selectedFiles: SelectedFile[];
  isModalOpened: boolean;
  isEditing: boolean;
  currentPost?: Post | null;
  updatePosts?: UpdatePosts;
  addFiles: AddFiles;
}) {
  const [editingFeed, setEditingFeed] = useState<number | null>(null);

  // Check if the post is ready for publishing (past the publish date)
  const isReadyForPublishing =
    currentPost &&
    currentPost.publish_at &&
    new Date(currentPost.publish_at) < new Date();

  return (
    <div className="">
      {feeds.map((feed) => (
        <div key={feed.id}>
          {editingFeed === feed.id ? (
            <PostCompose
              title={feed.name!}
              register={register}
              watch={watch}
              setValue={setValue}
              name={feed.slug!}
              canAutoPublish={feed.auto_publish!}
              postTypes={postTypes}
              typeMapping={feed.type_mapping as TypeMapping}
              feed={feed}
              selectedFiles={selectedFiles}
              isModalOpened={isModalOpened}
              editFeed={() => setEditingFeed(null)}
              addFiles={addFiles}
            />
          ) : (
            <PostDisplay
              feed={feed}
              watch={watch}
              setValue={setValue}
              editFeed={() => setEditingFeed(feed.id)}
              typeMapping={feed.type_mapping as TypeMapping}
              selectedFiles={selectedFiles}
              currentPost={currentPost || undefined}
              showPublishActions={!!isReadyForPublishing}
              addFiles={addFiles}
            />
          )}
        </div>
      ))}
    </div>
  );
}

function Footer({
  selected,
  watch,
  nextStep,
  step,
  isExisting,
  currentPost,
  onClose,
  isSaving,
  deletePost,
}: {
  watch: UseFormWatch<FieldValues>;
  selected: SelectedFile[];
  nextStep: () => void;
  step: Step;
  isExisting: boolean;
  currentPost: Post;
  onClose: () => void;
  isSaving: boolean;
  deletePost: () => void;
}) {
  return (
    <div className="flex justify-between items-center gap-4 w-full">
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="btn btn-neutral btn-outline btn-lg"
          onClick={onClose}
        >
          <XCircle size={24} />
          <span className="hidden md:block">
            {isExisting ? "Close" : "Cancel"}
          </span>
        </button>
        {currentPost && (
          <div className="dropdown dropdown-top">
            <div tabIndex={0} role="button" className="btn btn-ghost px-0">
              <MoreVertical />
            </div>
            <ul
              tabIndex={0}
              className="dropdown-content menu bg-base-100 rounded-box z-1 w-52 p-2 shadow-sm"
            >
              <li>
                <span onClick={deletePost}>
                  <Trash />
                  Delete Post
                </span>
              </li>
            </ul>
          </div>
        )}
      </div>

      <div className="flex  items-center justify-end ml-0! md:flex-row w-full">
        {step === 1 && (
          <button
            type="button"
            className="btn btn-secondary btn-lg"
            onClick={nextStep}
            disabled={
              (Object.keys(selected) ?? []).length === 0 ||
              (watch("default.post_content") ?? "").length === 0
            }
          >
            Next Step
          </button>
        )}
        {step === 2 && (
          <button
            type="submit"
            className="btn btn-secondary btn-lg"
            onClick={nextStep}
            disabled={isSaving}
          >
            {isSaving ? (
              <span className="loading loading-spinner loading-lg"></span>
            ) : (
              <Check />
            )}
            {isExisting ? "Update" : "Create"} Post
          </button>
        )}
      </div>
    </div>
  );
}

function getPossibleTypes(selected: SelectedFile[]) {
  const isCarousel = selected.length > 1;
  const firstFile = selected[0];

  if (isCarousel) {
    return ["CAROUSEL", "STORIES"];
  } else if (firstFile?.name?.includes(".video-thumbnail.jpg")) {
    return ["VIDEO", "REEL", "STORY-VIDEO"];
  } else if (firstFile?.mine_type?.startsWith("image/")) {
    return ["PHOTO", "STORY-PHOTO"];
  }

  return null;
}

function replaceVariants(
  content: string,
  feed: string,
  savedDescriptions: SavedContent[],
) {
  return content.replace(
    /\{\{VARIANTS\((.*?)\): "(.*?)"\}\}/gi,
    (_match: string | null | undefined, p1: string) => {
      const saved = savedDescriptions.find((s) => s.id.toString() === p1);
      console.log("VARIANT CHANGE", _match, p1, savedDescriptions);
      return saved?.variants[feed] ?? saved?.content ?? "";
    },
  );
}

function addHashtagsVariants(
  hashtags: string[],
  feed: string,
  savedDescriptions: SavedContent[],
) {
  console.log("ADD HASHTAGS VARIANTS", hashtags, feed, savedDescriptions);
  let newHashtags = [...hashtags];

  for (const hashtag of hashtags) {
    const match = hashtag.match(/#VARIANTS\((.*?)\)/i);
    if (match) {
      const saved = savedDescriptions.find((s) => s.id.toString() === match[1]);
      console.log("HASHTAG VARIANT CHANGE", hashtag, match[1], saved);
      newHashtags = [
        ...(saved?.hashtags.variants[feed]! ?? saved?.hashtags.main ?? []),
        ...newHashtags,
      ];
    }
  }

  // Remove the "VAIRIANTS" helpers
  newHashtags = newHashtags.filter((h) => !h.startsWith("#VARIANTS"));

  console.log("NEW HASHTAGS", newHashtags);

  return newHashtags;
}
